import { useState } from "react";
import { ActionMenu } from "@/components/ActionMenu";
import { NoteComposer } from "@/components/NoteComposer";
import { useToast } from "@/components/Toast";
import { phonesIn } from "@/lib/callLog";
import { formatAgo, formatDateTime } from "@/lib/format";
import { linkifyParts } from "@/lib/linkify";
import { type NoteLike, wasEdited } from "@/lib/notes";
import { cn } from "@/lib/utils";

/**
 * v2.2 P1.5 + P2: one note, used by the lead Notes tab and the Curative deal notes (drawer and page).
 *
 * v2.9 P2.1: the body is read-only — a click or double-click only follows a link. The ⋯ menu holds Edit
 * (v2.10: the NoteComposer — Enter or Save keeps it, Shift+Enter is a new line, Esc or Cancel drops it), Pin, Convert to call log,
 * Save phone(s) to card, Copy and Delete. Delete is soft and comes with a 5-second Undo on the toast
 * (undo = PATCH deleted_at null, which the API ledgers). URLs in the body are links (http/https only, never `javascript:`); nothing else in the body
 * is rendered as anything but text.
 */

export function NoteBody({ text }: { text: string }) {
  return (
    <p className="whitespace-pre-wrap break-words">
      {linkifyParts(text).map((part, i) =>
        part.kind === "link" ? (
          <a
            key={i}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:no-underline"
            onClick={(e) => e.stopPropagation()}
          >
            {part.text}
          </a>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </p>
  );
}

/** v2.9 P2.2: a note longer than this many lines shows them and a "more". */
const COLLAPSE_LINES = 6;

export function NoteItem({
  note,
  editable,
  onSave,
  onDelete,
  onRestore,
  onPin,
  onConvert,
  onSavePhones,
  pinned = false,
  badges,
  testId = "note",
}: {
  note: NoteLike;
  editable: boolean;
  onSave: (body: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  onRestore: () => Promise<unknown>;
  /** v2.9 P2.1: the ⋯ menu's Pin/Unpin, Convert to call log, Save phone(s) to card. */
  onPin?: (pinned: boolean) => Promise<unknown>;
  onConvert?: () => Promise<unknown>;
  onSavePhones?: (phones: string[]) => Promise<unknown>;
  pinned?: boolean;
  badges?: React.ReactNode;
  testId?: string;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const phones = phonesIn(note.body, note.kind);
  const long = note.body.split("\n").length > COLLAPSE_LINES || note.body.length > 600;

  const save = async () => {
    const next = draft.trim();
    if (!next || next === note.body) {
      setEditing(false);
      setDraft(note.body);
      return;
    }
    setBusy(true);
    try {
      await onSave(next);
      toast.push("Note saved");
      setEditing(false);
    } catch (e) {
      toast.push((e as Error).message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const act = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      toast.push(done);
    } catch (e) {
      toast.push((e as Error).message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onDelete();
      toast.push("Note deleted", "ok", {
        durationMs: 5000,
        action: {
          label: "Undo",
          onClick: () => {
            onRestore().then(
              () => toast.push("Note restored"),
              (e: Error) => toast.push(e.message, "danger"),
            );
          },
        },
      });
    } catch (e) {
      toast.push((e as Error).message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const edited = wasEdited(note);

  return (
    <li className="group rounded border px-2 py-1.5 text-xs" data-testid={testId} data-note-id={note.id}>
      <div className="mb-0.5 flex items-center gap-2 text-[11px] text-fg-muted">
        {badges}
        <span>{note.author_email ?? "—"}</span>
        <span>
          {formatDateTime(note.created_at)}
          {edited && note.updated_at && (
            <span title={formatDateTime(note.updated_at)} data-testid={`${testId}-edited`}>
              {" "}
              · edited {formatAgo(note.updated_at)}
            </span>
          )}
        </span>
        {!editing && (
          <span className="ml-auto" aria-busy={busy}>
            <ActionMenu
              testId={`${testId}-menu`}
              items={[
                { label: "Edit", testId: `${testId}-edit`, hidden: !editable, onSelect: () => {
                  setDraft(note.body);
                  setEditing(true);
                } },
                {
                  label: pinned ? "Unpin" : "Pin",
                  testId: `${testId}-pin`,
                  hidden: !editable || !onPin,
                  onSelect: () => void act(() => onPin!(!pinned), pinned ? "Unpinned" : "Pinned"),
                },
                {
                  label: "Convert to call log",
                  testId: `${testId}-convert`,
                  hidden: !editable || !onConvert,
                  onSelect: () => void act(() => onConvert!(), "Moved to the call log"),
                },
                {
                  label: phones.length > 1 ? `Save ${phones.length} phones to card` : "Save phone to card",
                  testId: `${testId}-save-phones`,
                  hidden: phones.length === 0 || !onSavePhones,
                  onSelect: () => void act(() => onSavePhones!(phones), "Saved to the card"),
                },
                {
                  label: "Copy",
                  testId: `${testId}-copy`,
                  onSelect: () => {
                    void navigator.clipboard?.writeText(note.body).catch(() => undefined);
                    toast.push("Copied");
                  },
                },
                { label: "Delete", testId: `${testId}-delete`, danger: true, hidden: !editable, onSelect: () => void remove() },
              ]}
            />
          </span>
        )}
      </div>
      {editing ? (
        // v2.10 P5.1 (D-138): the same composer as adding — Enter saves, Shift+Enter is a new line,
        // ⌘/Ctrl+Enter saves, Esc cancels; on a phone Return is a new line and Save saves.
        <NoteComposer
          testId={testId}
          ids={{ body: `${testId}-editor`, submit: `${testId}-save`, cancel: `${testId}-cancel` }}
          value={draft}
          onChange={setDraft}
          onSubmit={() => save()}
          onCancel={() => {
            setDraft(note.body);
            setEditing(false);
          }}
          submitLabel="Save"
          busy={busy}
          autoFocus
          placeholder="Edit the note…"
        />
      ) : (
        // v2.9 P2.1: clicking or double-clicking the text does nothing but follow a link — Edit is in ⋯.
        <div data-testid={`${testId}-text`}>
          <div className={cn(long && !expanded && "line-clamp-6")}>
            <NoteBody text={note.body} />
          </div>
          {long && (
            <button
              type="button"
              className="mt-0.5 text-[11px] text-accent underline"
              onClick={() => setExpanded((x) => !x)}
              data-testid={`${testId}-more`}
            >
              {expanded ? "less" : "more"}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
